
import React from 'react';
import { createPortal } from 'react-dom';
import { Language, AppMode } from '../types';
import { t } from '../locales';
import { FileText, Camera, Mic, LayoutPanelLeft, FileEdit } from 'lucide-react';

interface AddMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (method: 'audio' | 'text' | 'barcode' | 'camera') => void;
  language: Language;
  type: AppMode; // 'meal' | 'symptom' | 'recipe'
}

const AddMethodModal: React.FC<AddMethodModalProps> = ({ isOpen, onClose, onSelect, language, type }) => {
  if (!isOpen) return null;

  const handleSelect = (method: 'audio' | 'text' | 'barcode' | 'camera') => {
    onSelect(method);
  };

  const isSymptom = type === 'symptom';
  const isRecipe = type === 'recipe';

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-end justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity" onClick={onClose}></div>
      
      {/* Modal Content - Compact Bottom Sheet */}
      <div className="bg-white dark:bg-gray-900 w-full max-w-lg mx-auto rounded-t-2xl shadow-2xl p-5 relative z-10 animate-slideIn border-t border-gray-100 dark:border-gray-800 pb-safe">
        
        {/* Header with Close */}
        <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-bold text-gray-800 dark:text-white uppercase tracking-wider">{t(language, 'chooseInputMethod')}</h3>
            <button 
                onClick={onClose} 
                className="p-1.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
        </div>
        
        {/* Button Row */}
        <div className={`grid ${isRecipe ? 'grid-cols-2' : 'grid-cols-4'} gap-2 sm:gap-4`}>
            {/* Audio - Hidden for Recipe Mode */}
            {!isRecipe && (
                <button 
                    onClick={() => handleSelect('audio')}
                    className="flex flex-col items-center gap-2 p-2 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors active:scale-95 group"
                >
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white dark:bg-red-800 rounded-full flex items-center justify-center text-red-600 dark:text-red-100 shadow-sm group-hover:scale-110 transition-transform">
                        <Mic className="h-5 w-5 sm:h-6 sm:w-6" />
                    </div>
                    <span className="text-[10px] font-bold text-gray-700 dark:text-gray-300 text-center leading-tight">{t(language, 'inputAudio')}</span>
                </button>
            )}

            {/* Text / Manual */}
            <button 
                onClick={() => handleSelect('text')}
                className={`flex flex-col items-center gap-2 p-2 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors active:scale-95 group ${isRecipe ? 'py-6' : ''}`}
            >
                <div className={`${isRecipe ? 'w-14 h-14' : 'w-10 h-10 sm:w-12 sm:h-12'} bg-white dark:bg-emerald-800 rounded-full flex items-center justify-center text-emerald-600 dark:text-emerald-100 shadow-sm group-hover:scale-110 transition-transform`}>
                    {isRecipe ? <FileEdit size={24} /> : <FileText className="h-5 w-5 sm:h-6 sm:w-6" />}
                </div>
                <span className="text-[10px] font-bold text-gray-700 dark:text-gray-300 text-center leading-tight">
                    {isRecipe ? t(language, 'inputManualRecipe') : t(language, 'inputText')}
                </span>
            </button>

            {/* Camera - Enabled for Recipe Mode */}
            <button 
                onClick={() => handleSelect('camera')} 
                disabled={isSymptom}
                className={`flex flex-col items-center gap-2 p-2 rounded-xl border transition-colors active:scale-95 group ${isSymptom ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-800 hover:bg-blue-100'} ${isRecipe ? 'py-6' : ''}`}
            >
                <div className={`${isRecipe ? 'w-14 h-14' : 'w-10 h-10 sm:w-12 sm:h-12'} rounded-full flex items-center justify-center shadow-sm bg-white dark:bg-blue-800 text-blue-600 dark:text-blue-100 group-hover:scale-110 transition-transform`}>
                    <Camera className={isRecipe ? 'h-6 w-6' : 'h-5 w-5 sm:h-6 sm:w-6'} />
                </div>
                <div className="flex flex-col items-center">
                    <span className="text-[10px] font-bold text-gray-700 dark:text-gray-300 text-center leading-tight">
                        {isRecipe ? t(language, 'inputCameraRecipe') : t(language, 'inputCamera')}
                    </span>
                    {!isRecipe && <span className="text-[8px] font-medium text-gray-400 uppercase tracking-wide mt-0.5">{t(language, 'comingSoon')}</span>}
                </div>
            </button>

            {/* Barcode - Hidden for Recipe Mode */}
            {!isRecipe && (
                <button 
                    onClick={() => handleSelect('barcode')}
                    disabled={isSymptom}
                    className={`flex flex-col items-center gap-2 p-2 rounded-xl border transition-colors active:scale-95 group ${
                        isSymptom
                        ? 'bg-gray-50 dark:bg-gray-800 border-gray-100 dark:border-gray-700 opacity-50 cursor-not-allowed'
                        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                >
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform ${
                        isSymptom ? 'bg-gray-200 dark:bg-gray-700 text-gray-500' : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-white border border-gray-200 dark:border-gray-600'
                    }`}>
                        <LayoutPanelLeft className="h-5 w-5 sm:h-6 sm:w-6" />
                    </div>
                    <span className="text-[10px] font-bold text-gray-700 dark:text-gray-300 text-center leading-tight">{t(language, 'inputBarcode')}</span>
                </button>
            )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddMethodModal;
